import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    Loader2, RefreshCw, TrendingUp, TrendingDown, Wallet, Users, Building2,
    CreditCard, AlertTriangle, CheckCircle2, Info, ArrowDownRight, ArrowUpRight,
    Crown, Activity, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eur = (n) =>
    `${(Number(n) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;
const eurFine = (n) =>
    `${(Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const SOURCE_LABEL = {
    'campus-public': 'Campus 360°',
    'diagnostic-quiz': 'Diagnostic',
    'landing': 'Landing',
    'donate': 'Donation',
    'cms': 'Article CMS',
    'footer': 'Footer',
    'contact-form': 'Contact',
    'direct': 'Direct',
};
const PLAN_LABEL = {
    'starter-yearly': 'Starter (annuel)',
    'pro-yearly': 'Pro (annuel)',
    'starter-monthly': 'Starter (mensuel)',
    'pro-monthly': 'Pro (mensuel)',
};

const ALERT_STYLES = {
    danger: { icon: AlertTriangle, cls: 'bg-rose-50 border-rose-200 text-rose-800', tone: 'text-rose-600' },
    warn: { icon: AlertTriangle, cls: 'bg-amber-50 border-amber-200 text-amber-800', tone: 'text-amber-600' },
    info: { icon: Info, cls: 'bg-blue-50 border-blue-200 text-blue-800', tone: 'text-blue-600' },
    success: { icon: CheckCircle2, cls: 'bg-emerald-50 border-emerald-200 text-emerald-800', tone: 'text-emerald-600' },
};

// ---------- Small visual primitives ----------
function KpiCard({ icon: Icon, label, value, sub, trend, accent = 'primary', testid }) {
    const accents = {
        primary: 'bg-primary-light text-primary',
        emerald: 'bg-emerald-50 text-emerald-700',
        amber: 'bg-amber-50 text-amber-700',
        rose: 'bg-rose-50 text-rose-700',
        violet: 'bg-violet-50 text-violet-700',
    };
    return (
        <div
            className="rounded-2xl border border-stone-200 bg-white p-5 hover:shadow-sm transition-shadow"
            data-testid={testid}
        >
            <div className="flex items-center justify-between">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${accents[accent]}`}>
                    <Icon size={18} strokeWidth={1.8} />
                </div>
                {trend != null && (
                    <span
                        className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full border ${
                            trend > 0
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : trend < 0
                                ? 'bg-rose-50 text-rose-700 border-rose-200'
                                : 'bg-stone-50 text-stone-600 border-stone-200'
                        }`}
                    >
                        {trend > 0 ? <ArrowUpRight size={12} /> : trend < 0 ? <ArrowDownRight size={12} /> : null}
                        {trend > 0 ? '+' : ''}
                        {trend}%
                    </span>
                )}
            </div>
            <div className="mt-4">
                <div className="text-[11px] uppercase tracking-wider text-foreground/50">{label}</div>
                <div className="font-heading text-3xl mt-1" data-testid={testid ? `${testid}-value` : undefined}>
                    {value}
                </div>
                {sub && <div className="text-xs text-foreground/60 mt-1">{sub}</div>}
            </div>
        </div>
    );
}

// Visual funnel: 3 stacked horizontal bars (lead → workspace → paid)
function FunnelChart({ funnel }) {
    const steps = [
        { key: 'leads', label: 'Demandes d\'accès', value: funnel.leads_total, color: '#3b82f6', icon: Users },
        { key: 'ws', label: 'Espaces créés', value: funnel.workspaces_total, color: '#8b5cf6', icon: Building2 },
        { key: 'paid', label: 'Paiements finalisés', value: funnel.paid_count, color: '#10b981', icon: CreditCard },
    ];
    const max = Math.max(...steps.map((s) => s.value), 1);

    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="exec-funnel">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="eyebrow mb-1">Entonnoir bout-en-bout</div>
                    <h3 className="font-heading text-xl">Visiteur → Lead → Espace → Paiement</h3>
                </div>
                <Activity className="text-foreground/30" size={20} />
            </div>

            <div className="space-y-4">
                {steps.map((s, idx) => {
                    const widthPct = (s.value / max) * 100;
                    const Icon = s.icon;
                    return (
                        <div key={s.key} data-testid={`funnel-step-${s.key}`}>
                            <div className="flex items-center justify-between text-xs mb-1.5">
                                <div className="flex items-center gap-2 text-foreground/70">
                                    <Icon size={14} />
                                    <span className="font-medium">{s.label}</span>
                                </div>
                                <span className="font-mono text-foreground">{s.value.toLocaleString('fr-FR')}</span>
                            </div>
                            <div className="relative h-9 rounded-lg bg-stone-100 overflow-hidden">
                                <div
                                    className="h-full rounded-lg transition-all duration-700 ease-out"
                                    style={{ width: `${Math.max(widthPct, 4)}%`, backgroundColor: s.color }}
                                />
                            </div>
                            {idx < steps.length - 1 && (
                                <div className="flex items-center justify-center mt-2 text-[10px] uppercase tracking-wider text-foreground/40">
                                    <ArrowDownRight size={10} className="mr-1" />
                                    {idx === 0
                                        ? `${funnel.lead_to_ws_pct}% conversion`
                                        : `${funnel.ws_to_paid_pct}% conversion`}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3 pt-4 border-t border-stone-100">
                <FunnelRate label="Lead → Espace" value={funnel.lead_to_ws_pct} testid="rate-lead-ws" />
                <FunnelRate label="Espace → Payé" value={funnel.ws_to_paid_pct} testid="rate-ws-paid" />
                <FunnelRate label="Lead → Payé" value={funnel.lead_to_paid_pct} testid="rate-lead-paid" highlight />
            </div>
        </div>
    );
}

function FunnelRate({ label, value, highlight, testid }) {
    const tone =
        value >= 20 ? 'text-emerald-700' : value >= 10 ? 'text-amber-700' : 'text-stone-600';
    return (
        <div
            className={`text-center p-3 rounded-xl ${highlight ? 'bg-primary-light/40 border border-primary/20' : 'bg-stone-50'}`}
            data-testid={testid}
        >
            <div className="text-[10px] uppercase tracking-wider text-foreground/50 mb-1">{label}</div>
            <div className={`font-heading text-2xl ${tone}`}>{value}%</div>
        </div>
    );
}

// 30-day revenue sparkline (inline SVG)
function RevenueSparkline({ series }) {
    if (!series || !series.length) return null;
    const W = 600;
    const H = 120;
    const pad = 6;
    const vals = series.map((d) => d.revenue);
    const max = Math.max(...vals, 1);
    const xStep = (W - pad * 2) / Math.max(series.length - 1, 1);

    const points = series.map((d, i) => {
        const x = pad + i * xStep;
        const y = H - pad - (d.revenue / max) * (H - pad * 2);
        return [x, y];
    });
    const pathD = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
    const areaD = `${pathD} L ${points[points.length - 1][0].toFixed(1)} ${H - pad} L ${pad} ${H - pad} Z`;

    const total = vals.reduce((a, b) => a + b, 0);

    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="exec-sparkline">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="eyebrow mb-1">Revenus 30 derniers jours</div>
                    <h3 className="font-heading text-xl">{eur(total)} encaissés</h3>
                </div>
                <TrendingUp className="text-emerald-500/40" size={20} />
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-28">
                <defs>
                    <linearGradient id="exec-spark-grad" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#C8553D" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#C8553D" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <path d={areaD} fill="url(#exec-spark-grad)" />
                <path d={pathD} fill="none" stroke="#C8553D" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            </svg>
            <div className="flex items-center justify-between text-[10px] text-foreground/50 mt-2 px-1">
                <span>{series[0]?.date}</span>
                <span>{series[series.length - 1]?.date}</span>
            </div>
        </div>
    );
}

function AlertCard({ alert }) {
    const meta = ALERT_STYLES[alert.level] || ALERT_STYLES.info;
    const Icon = meta.icon;
    return (
        <div className={`rounded-xl border p-4 flex gap-3 ${meta.cls}`} data-testid={`exec-alert-${alert.level}`}>
            <Icon size={18} className={`shrink-0 mt-0.5 ${meta.tone}`} />
            <div className="text-sm">
                <div className="font-medium">{alert.title}</div>
                <div className="opacity-80 text-xs mt-0.5">{alert.detail}</div>
            </div>
        </div>
    );
}

// ---------- Main component ----------
export default function ExecutiveDashboardAdmin({ auth }) {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [data, setData] = useState(null);

    const load = async () => {
        setRefreshing(true);
        try {
            const r = await axios.get(`${API}/super-admin/executive-dashboard`, auth);
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Chargement du tableau de bord échoué');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, []);

    const generatedAt = useMemo(() => {
        if (!data?.generated_at) return '';
        try {
            return new Date(data.generated_at).toLocaleString('fr-FR');
        } catch {
            return data.generated_at;
        }
    }, [data]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20" data-testid="exec-loading">
                <Loader2 className="animate-spin" />
            </div>
        );
    }
    if (!data) {
        return (
            <div className="text-center py-20 text-foreground/60" data-testid="exec-empty">
                Aucune donnée disponible.
            </div>
        );
    }

    const k = data.kpis;

    return (
        <div className="space-y-6" data-testid="executive-dashboard">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="eyebrow mb-1 flex items-center gap-2">
                        <Crown size={12} /> Comité d'investissement
                    </div>
                    <h2 className="font-heading text-2xl">Tableau de bord exécutif</h2>
                    <p className="text-xs text-foreground/50 mt-1">Mis à jour le {generatedAt}</p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={load}
                    disabled={refreshing}
                    data-testid="exec-refresh-btn"
                >
                    <RefreshCw size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                    Rafraîchir
                </Button>
            </div>

            {/* Alerts strip */}
            {data.alerts && data.alerts.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="exec-alerts">
                    {data.alerts.map((a, i) => (
                        <AlertCard key={i} alert={a} />
                    ))}
                </div>
            )}

            {/* Primary KPIs */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard
                    icon={Wallet}
                    label="MRR équivalent"
                    value={eur(k.mrr_equivalent)}
                    sub={`${eurFine(k.rev_total)} cumulé`}
                    accent="primary"
                    testid="kpi-mrr"
                />
                <KpiCard
                    icon={TrendingUp}
                    label="Revenus ce mois"
                    value={eur(k.rev_this_month)}
                    sub={`Mois -1 : ${eur(k.rev_last_month)}`}
                    trend={k.mom_growth_pct}
                    accent="emerald"
                    testid="kpi-revenue-month"
                />
                <KpiCard
                    icon={CreditCard}
                    label="Paiements finalisés"
                    value={k.paid_count}
                    sub={`Abandon : ${k.abandon_rate_pct}%`}
                    accent={k.abandon_rate_pct >= 60 ? 'rose' : 'violet'}
                    testid="kpi-paid-count"
                />
                <KpiCard
                    icon={Users}
                    label="Demandes d'accès"
                    value={k.leads_total}
                    sub={`${k.leads_30d} sur 30j · ${k.leads_converted} converties`}
                    accent="amber"
                    testid="kpi-leads"
                />
            </div>

            {/* Funnel + sparkline */}
            <div className="grid lg:grid-cols-2 gap-6">
                <FunnelChart funnel={data.funnel} />
                <RevenueSparkline series={data.revenue_30d_series} />
            </div>

            {/* Sources + plan breakdown */}
            <div className="grid lg:grid-cols-2 gap-6">
                <div className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="exec-sources">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="eyebrow mb-1">Acquisition</div>
                            <h3 className="font-heading text-xl">Top sources des leads</h3>
                        </div>
                        <Sparkles className="text-foreground/30" size={20} />
                    </div>
                    {data.top_sources.length === 0 ? (
                        <div className="text-sm text-foreground/50 text-center py-6">
                            Aucune source enregistrée.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {data.top_sources.map((s) => {
                                const pct = k.leads_total ? (s.count / k.leads_total) * 100 : 0;
                                return (
                                    <div key={s.source} data-testid={`source-${s.source}`}>
                                        <div className="flex items-center justify-between text-xs mb-1">
                                            <span className="font-medium">{SOURCE_LABEL[s.source] || s.source}</span>
                                            <span className="font-mono text-foreground/70">
                                                {s.count} · {pct.toFixed(0)}%
                                            </span>
                                        </div>
                                        <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                                            <div
                                                className="h-full bg-primary transition-all duration-700"
                                                style={{ width: `${Math.max(pct, 2)}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="exec-plans">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="eyebrow mb-1">Mix produit</div>
                            <h3 className="font-heading text-xl">Répartition par plan</h3>
                        </div>
                        <Crown className="text-foreground/30" size={20} />
                    </div>
                    {data.plans_breakdown.length === 0 ? (
                        <div className="text-sm text-foreground/50 text-center py-6">
                            Aucun paiement enregistré pour le moment.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {data.plans_breakdown.map((p) => (
                                <div
                                    key={p.plan_id}
                                    className="flex items-center justify-between border border-stone-100 rounded-xl px-4 py-3 hover:border-stone-200 transition-colors"
                                    data-testid={`plan-${p.plan_id}`}
                                >
                                    <div>
                                        <div className="text-sm font-medium">{PLAN_LABEL[p.plan_id] || p.plan_id}</div>
                                        <div className="text-[11px] text-foreground/50 mt-0.5">{p.count} client(s)</div>
                                    </div>
                                    <div className="font-mono text-sm font-semibold text-primary">
                                        {eur(p.revenue)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Footer note */}
            <div className="text-[11px] text-foreground/40 text-center pt-2">
                MRR équivalent = revenus cumulés ÷ 12 (proxy pour plans annuels one-shot).
                Les abandons incluent les checkouts <em>initiated / pending / expired</em>.
            </div>
        </div>
    );
}
